import React from 'react';
import { formatTestId } from '../../../../utils/test-id/format-testid';
import { Image } from '../../Image/Image';
import { Icon } from '../Icon/Icon';
import { NavigationItemType } from '../itemTypes';
import { Category } from '../../constants';
import styles from './NavLink.scss';
import classNames from 'classnames';

const getImageDimensions = (itemType: NavigationItemType, index: number) => {
  switch (itemType) {
    case NavigationItemType.BRAND_LOGOS:
      return { width: 221, height: 86 };
    case NavigationItemType.IMAGE_GRID:
      return { width: 240, height: 240 };
    case NavigationItemType.WALL:
      return index === 0 || index === 5
        ? { width: 313, height: 416 }
        : { width: 154, height: 206 };
    case NavigationItemType.FEATURED:
      return { width: 220, height: 220 };
    default:
      return { width: 90, height: 90 };
  }
};

enum IMAGE_TYPE {
  FULL_IMAGE = 'fullImage',
  THUMBNAIL = 'thumbnail',
  ICON = 'icon',
  NONE = 'none',
}

const getImageType = (itemType: NavigationItemType, imageUrl?: string) => {
  if (itemType === NavigationItemType.EXTRAS) return IMAGE_TYPE.ICON;
  if (itemType === NavigationItemType.DEFAULT) {
    if (imageUrl) return IMAGE_TYPE.THUMBNAIL;
    return IMAGE_TYPE.NONE;
  }
  return IMAGE_TYPE.FULL_IMAGE;
};

type NavLinkProps = {
  index: number;
  label: string;
  url?: string;
  handleClick: () => void;
  tab?: string;
  imageUrl?: string;
  itemType: NavigationItemType;
  hierarchy: string[];
  tabIndex?: number;
};

export const NavLink = ({
  index,
  label,
  url,
  handleClick,
  tab,
  imageUrl,
  itemType,
  hierarchy,
  tabIndex,
}: NavLinkProps) => {
  const getTestId = formatTestId(
    label,
    tab === Category.MONEY ? 'services-' : 'nav-'
  );
  const Wrapper = url ? 'a' : 'button';

  const isExternal = url && !url.startsWith('/');

  const imageType = getImageType(itemType, imageUrl);

  const hasLabelOverlay =
    imageType === IMAGE_TYPE.FULL_IMAGE &&
    itemType !== NavigationItemType.BRAND_LOGOS;

  const anchorProps = {
    href: url,
    target: isExternal ? '_blank' : undefined,
    rel: isExternal ? 'noreferrer' : undefined,
  };

  const ariaLabel = label.endsWith('*')
    ? `${label.replace('*', '')} selected lines only`
    : label;

  const { width, height } = getImageDimensions(itemType, index);

  /* hack for A/B test to match control */
  if (tab === Category.MONEY) {
    hierarchy.push('John Lewis Money');
  }

  return (
    <Wrapper
      onClick={handleClick}
      {...(url ? anchorProps : {})}
      tabIndex={tabIndex}
      data-testid={getTestId}
      aria-label={ariaLabel}
      data-hierarchy={hierarchy.join('|')}
      className={classNames(
        styles.navLink,
        styles[itemType],
        styles[imageType]
      )}
    >
      {!!imageUrl && (
        <Image url={imageUrl} label={label} width={width} height={height} />
      )}
      {imageType === IMAGE_TYPE.ICON && <Icon label={label} />}
      {hasLabelOverlay && (
        <div className={styles.labelOverlay}>
          <span>{label}</span>
        </div>
      )}
      {imageType !== IMAGE_TYPE.FULL_IMAGE ? label : ''}
    </Wrapper>
  );
};
